// Auto-detect knowledge base (BL592).
//
// An extensible, *pure* registry of stack-detection signatures. Each signature
// pairs a set of match rules (files / package.json deps / requirements.txt
// entries) with the hints and known pitfalls for that stack. The auto-detect
// engine (BL584 M2) consults the registry after its own classification and
// surfaces the matches as `detection.signals` + `detection.pitfalls`, so a user
// sees a stack's known failure mode *before* the build hits it.
//
// This file is data + a pure matcher only. It performs no I/O: callers build a
// `RepoSnapshot` (the node-only folder read lives in folder-scan.ts) and pass
// it in. `findMatchingSignatures` is deterministic — same snapshot in, same
// signatures out, in registry order.
//
// The seed content is the five plug-failure classes audited on 2026-06-25
// (docs/audits/2026-06-25-plug-failures-and-org-investigation.md). New
// signatures land here as PRs — from brain or the helper-brain
// cloudgrid-io/skills repo — without touching the engine.

import type { AppFramework } from './types.js';

/**
 * Minimal parsed `package.json` shape the matcher reads. Unknown fields are
 * preserved but ignored; only dependency maps and `type` are consulted.
 */
export interface PackageJsonShape {
  type?: string;
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
  [key: string]: unknown;
}

/**
 * What the knowledge base reads from. A flat, already-gathered view of a repo —
 * no filesystem handle, so the matcher stays pure. The node-only builder
 * (folder-scan.ts) populates `package_json` / `requirements_txt` when it can
 * read them; an `entries`-only caller leaves them undefined and still gets
 * file-presence matches.
 */
export interface RepoSnapshot {
  /**
   * Relative file paths present in the repo (top level + one level deep, the
   * same depth the engine scans). e.g. `package.json`, `prisma/schema.prisma`.
   */
  files: string[];
  /** Parsed top-level `package.json`, if present and valid JSON. */
  package_json?: PackageJsonShape | null;
  /** Raw `requirements.txt` contents, if present. */
  requirements_txt?: string | null;
}

/** A pitfall a stack is known to hit, surfaced to the user as a heads-up. */
export interface StackPitfall {
  id: string;
  /** Human-readable trigger. */
  condition: string;
  /** Why it bites and how to avoid it. */
  explanation: string;
  /** Optional pointer to the tracking backlog. */
  backlog_ref?: string;
}

/**
 * One stack signature. ALL rules in `match` must hold for the signature to
 * apply; `hints` are then surfaced to the deployer / CLI.
 */
export interface StackSignature {
  /** Stable kebab-case id, e.g. `nextjs-strict-ts`. */
  id: string;
  /** The framework this signature describes. */
  framework: AppFramework;
  /** Match rules — every present rule must hold (logical AND). */
  match: {
    /** File paths that must all exist (top level or one level deep). */
    files?: string[];
    package_json?: {
      /** Dependency keys that must all exist (in dependencies or devDependencies). */
      dependencies?: string[];
    };
    /** Package names that must all appear in requirements.txt. */
    requirements_txt_includes?: string[];
  };
  /** Hints surfaced to the deployer / CLI once the signature matches. */
  hints: {
    needs?: string[];
    build_command?: string;
    start_command?: string;
    port?: number;
    common_pitfalls?: StackPitfall[];
  };
}

/**
 * Seed registry — the five plug-failure classes from the 2026-06-25 audit.
 * Order is the surfacing order; keep the most specific signatures first.
 */
export const STACK_SIGNATURES: StackSignature[] = [
  {
    // Michal's first-deploy face-plant: the deployer's synthesized
    // standalone-wrap (`next.config.ts`) fails `next build`'s strict-TS
    // type-check. The `.ts` config is the trigger — emit it non-type-checked.
    id: 'nextjs-strict-ts',
    framework: 'nextjs',
    match: {
      files: ['next.config.ts'],
      package_json: { dependencies: ['next'] },
    },
    hints: {
      needs: ['node'],
      build_command: 'next build',
      start_command: 'next start -p $PORT',
      port: 3000,
      common_pitfalls: [
        {
          id: 'standalone-wrap-strict-ts',
          condition: 'next.config.ts under a strict tsconfig',
          explanation:
            'The deployer wraps your next.config.ts to inject standalone output. ' +
            'Because your config is typed NextConfig, the wrapper fails next build’s ' +
            'strict type-check. The fix is on the platform side (emit the wrapper as ' +
            '.mjs / .js or with type-checking off); no change to your code is needed.',
          backlog_ref: 'docs/backlog/542-standalone-output-special-case.md',
        },
      ],
    },
  },
  {
    // Tamir's t13-nextjs-guestbook ×5: `prisma generate` (usually a postinstall)
    // fails with "Could not find a schema.prisma file" when prisma/schema.prisma
    // is absent or lives elsewhere.
    id: 'prisma-schema-required',
    framework: 'other',
    match: {
      package_json: { dependencies: ['prisma'] },
    },
    hints: {
      needs: ['node'],
      common_pitfalls: [
        {
          id: 'prisma-generate-no-schema',
          condition: 'prisma in dependencies with no prisma/schema.prisma',
          explanation:
            'prisma generate (often a postinstall script) needs a schema at ' +
            'prisma/schema.prisma. If your schema lives elsewhere, point to it with ' +
            'the prisma.schema field in package.json, or move it to prisma/schema.prisma. ' +
            'Without it the build fails with "Could not find a schema.prisma file".',
        },
      ],
    },
  },
  {
    // Tamir's t18-nuxt: `nuxt prepare` ESM module-resolution failure. ESM-only
    // deps in nuxt.config.ts need "type":"module" in package.json.
    id: 'nuxt-esm-config',
    framework: 'nuxt',
    match: {
      files: ['nuxt.config.ts'],
      package_json: { dependencies: ['nuxt'] },
    },
    hints: {
      needs: ['node'],
      build_command: 'nuxt build',
      start_command: 'node .output/server/index.mjs',
      port: 3000,
      common_pitfalls: [
        {
          id: 'nuxt-esm-needs-type-module',
          condition: 'ESM-only deps without "type":"module" in package.json',
          explanation:
            'nuxt.config.ts and ESM-only dependencies resolve as ES modules. If ' +
            'package.json does not declare "type":"module", nuxt prepare fails with ' +
            'ERR_MODULE_NOT_FOUND during install. Add "type":"module" to package.json.',
        },
      ],
    },
  },
  {
    // Tamir's t18-nuxt-catalog: `npm ci` aborts when package.json and
    // package-lock.json disagree (commander@11 in lock ≠ @13 required).
    id: 'node-lockfile-sync',
    framework: 'other',
    match: {
      files: ['package.json', 'package-lock.json'],
    },
    hints: {
      needs: ['node'],
      common_pitfalls: [
        {
          id: 'npm-ci-lockfile-drift',
          condition: 'package.json and package-lock.json out of sync',
          explanation:
            'The build runs npm ci, which refuses to install when package.json and ' +
            'package-lock.json disagree ("can only install packages when your ' +
            'package.json and package-lock.json are in sync"). Run npm install locally ' +
            'to regenerate the lockfile, then commit it.',
        },
      ],
    },
  },
  {
    // Tamir's t16-streamlit: built fine, then crashlooped — the synthesized
    // Dockerfile ran `python app.py` instead of `streamlit run`, so Streamlit
    // boots in bare mode ("missing ScriptRunContext") and never serves.
    id: 'streamlit-app',
    framework: 'streamlit',
    match: {
      requirements_txt_includes: ['streamlit'],
    },
    hints: {
      needs: ['python'],
      start_command:
        'streamlit run app.py --server.port=$PORT --server.address=0.0.0.0',
      port: 8501,
      common_pitfalls: [
        {
          id: 'streamlit-needs-streamlit-run',
          condition: 'started with python app.py instead of streamlit run',
          explanation:
            'Streamlit must be launched with "streamlit run <entry>.py", not ' +
            '"python <entry>.py". Run bare, it prints "missing ScriptRunContext" and ' +
            'never binds a port, so the rollout crashloops. Common entry files: ' +
            'app.py, main.py, streamlit_app.py.',
        },
      ],
    },
  },
];

// --- Pure matcher -----------------------------------------------------------

/** True when `path` is present in the snapshot at its exact path or nested one+ levels deep. */
function fileIsPresent(files: string[], path: string): boolean {
  const suffix = `/${path}`;
  return files.some((f) => f === path || f.endsWith(suffix));
}

/** Merge `dependencies` + `devDependencies` keys from a parsed package.json. */
function dependencyKeys(pkg: PackageJsonShape | null | undefined): Set<string> {
  const keys = new Set<string>();
  if (!pkg) return keys;
  for (const map of [pkg.dependencies, pkg.devDependencies]) {
    if (map && typeof map === 'object') {
      for (const key of Object.keys(map)) keys.add(key);
    }
  }
  return keys;
}

/**
 * Extract the bare package name from a requirements.txt line, dropping version
 * specifiers, extras, environment markers, and comments. Returns '' for blank,
 * comment, or option lines (`-r base.txt`).
 */
function requirementName(line: string): string {
  const stripped = line.split('#', 1)[0].trim();
  if (stripped === '' || stripped.startsWith('-')) return '';
  // Name ends at the first version/extra/marker delimiter.
  const name = stripped.split(/[=<>!~;[\s]/, 1)[0];
  return name.trim().toLowerCase();
}

/** Parse requirements.txt contents into a lowercase set of package names. */
function requirementNames(contents: string | null | undefined): Set<string> {
  const names = new Set<string>();
  if (!contents) return names;
  for (const line of contents.split(/\r?\n/)) {
    const name = requirementName(line);
    if (name) names.add(name);
  }
  return names;
}

/** True when every rule in the signature's `match` block holds for the snapshot. */
function signatureMatches(sig: StackSignature, repo: RepoSnapshot): boolean {
  const { files, package_json, requirements_txt_includes } = sig.match;

  if (files && !files.every((f) => fileIsPresent(repo.files, f))) {
    return false;
  }

  if (package_json?.dependencies && package_json.dependencies.length > 0) {
    const deps = dependencyKeys(repo.package_json);
    if (!package_json.dependencies.every((d) => deps.has(d))) {
      return false;
    }
  }

  if (requirements_txt_includes && requirements_txt_includes.length > 0) {
    const reqs = requirementNames(repo.requirements_txt);
    if (!requirements_txt_includes.every((r) => reqs.has(r.toLowerCase()))) {
      return false;
    }
  }

  return true;
}

/**
 * Return every signature whose match rules ALL hold for `repo`, in registry
 * order. Pure and deterministic — no I/O, no clock, no randomness. A signature
 * with an empty `match` block never matches (it would apply to everything),
 * which the seed registry never does.
 */
export function findMatchingSignatures(repo: RepoSnapshot): StackSignature[] {
  return STACK_SIGNATURES.filter((sig) => hasAnyRule(sig) && signatureMatches(sig, repo));
}

/** Guard: a signature must declare at least one match rule to be considered. */
function hasAnyRule(sig: StackSignature): boolean {
  const { files, package_json, requirements_txt_includes } = sig.match;
  return (
    (files !== undefined && files.length > 0) ||
    (package_json?.dependencies !== undefined && package_json.dependencies.length > 0) ||
    (requirements_txt_includes !== undefined && requirements_txt_includes.length > 0)
  );
}
